// AxisInspector.js — the E5 Axis Inspector panel (forest → tree). Renders the PERSISTED, merged Inspector
// object for one axis (never recomputes). Master-detail: LEFT = ranked Issues + Protect list (the forest);
// RIGHT = the selected item's diagnosis opening onto the actual film (the tree). Used in two mounts: below
// the timeline (wide card) and in the Table-view drawer — same component, so they can't drift.
//
// Data contract (merged): { quick_read{band,score,owed_status,hover_summary}, overall_read{paragraphs[]},
//   pattern_coverage{headline_coverage}, problem_patterns[], strength_patterns[] } where each pattern carries
//   { candidate_id, title, plain_pattern, why_it_matters, narration_status, primary_grain, seen_in[{unit_ref}],
//     representative_example{label,unit_ref,quote,example_read}, contrast_example{quote,contrast_read},
//     action{action,test}, questions_for_ai[{mode,question,question_id,linked_refs}], mechanism_family }.

import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { fmtScore } from './Constants';   // one score formatter — no bare toFixed(1)

const NAVY = '#14536e';
const INK = '#0f172a';
const MUTE = '#64748b';
const LINE = '#e7edf1';

// band → colour + word (matches the timeline's green→red family; broken red, soft amber, solid green)
const BAND = {
  broken: { c: '#c23b34', w: 'weak' },
  soft: { c: '#9a6700', w: 'soft' },
  solid: { c: '#2AA147', w: 'strong' },
  not_owed: { c: MUTE, w: 'not owed' },
};

const GRAIN_WORD = { scene: 'scene', sequence: 'sequence', act: 'act', script: 'script' };

// A ref's from_to are ALWAYS scene numbers (a sequence "1_9" spans scenes 1–9), so a ref only ever yields a
// SCENE label — never "Seq 1–9" (which would read as sequence ordinals). Authoritative sequence/act names
// come from the deterministic LABELS (see locLabel), not from refs.
function refLabel(ref) {
  const m = /der:unit:(scene|sequence|act):(\d+)_(\d+)/.exec(ref || '');
  if (!m) return ref || '';
  const [, , a, b] = m;
  return a === b ? `Scene ${a}` : `Scenes ${a}–${b}`;
}

// Parse a deterministic unit label like "Act 2a, Seq 1: Arrival in America" → { act:'2a', seq:1 }.
function parseLoc(label) {
  const m = /Act\s+(\S+?),\s*Seq\s+(\d+)/i.exec(label || '');
  return m ? { act: m[1], seq: +m[2] } : null;
}
// AUTHORITATIVE location for a pattern, from its seen_in LABELS (act + within-act sequence ordinal):
// "Act 2a · Seq 1", "Act 1 · Seq 3–5", or "Acts 1–3" when it spans acts. Falls back to the count for
// scene-grain patterns whose labels carry no act.
function locLabel(item) {
  const parsed = (item.seen_in || []).map(s => parseLoc(s.label)).filter(Boolean);
  if (parsed.length) {
    const acts = [...new Set(parsed.map(p => p.act))];
    if (acts.length === 1) {
      const seqs = parsed.map(p => p.seq).sort((a, b) => a - b);
      const lo = seqs[0], hi = seqs[seqs.length - 1];
      return `Act ${acts[0]} · Seq ${lo === hi ? lo : `${lo}–${hi}`}`;
    }
    return `Acts ${acts[0]}–${acts[acts.length - 1]}`;
  }
  return scopeLabel(item);
}

// a pattern's cross-grain footprint (its own members) as unit_refs, for "show on timeline"
function patternRefs(p) {
  const refs = (p.seen_in || []).map(s => s.unit_ref).filter(Boolean);
  const rep = p.representative_example?.unit_ref;
  if (rep && !refs.includes(rep)) refs.push(rep);
  return refs;
}

// compact scope line for a pattern row: "30 scenes" / "Seq 1–2" etc.
function scopeLabel(p) {
  const refs = (p.seen_in || []);
  const grain = p.primary_grain || 'scene';
  if (refs.length <= 1) return refLabel(refs[0]?.unit_ref || p.representative_example?.unit_ref);
  // contiguous run at sequence/act → range; scattered scenes → count
  return `${refs.length} ${GRAIN_WORD[grain] || grain}${refs.length === 1 ? '' : 's'}`;
}

// the best display label for a pattern: its behavioral title if narrated, else the deterministic
// representative label (proper "Act 2a, Seq 1"), else the authoritative location.
function itemLabel(item) {
  return item.title || item.representative_example?.label || locLabel(item);
}

function BandPill({ band, score }) {
  const b = BAND[band] || BAND.not_owed;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
      <span style={{ width: 9, height: 9, borderRadius: '50%', background: b.c, display: 'inline-block' }} />
      <span style={{ color: b.c, fontWeight: 700, fontSize: 13, textTransform: 'lowercase' }}>{b.w}</span>
      {score != null && <span style={{ color: MUTE, fontWeight: 600, fontSize: 13 }}>· {fmtScore(score)}</span>}
    </span>
  );
}

// ---- LEFT: one issue / strength row -------------------------------------------------------------
function ListRow({ item, index, kind, selected, onSelect }) {
  const mv = item.narration_status === 'machine_view';
  const isMain = kind === 'issue' && index === 0;
  return (
    <button type="button" onClick={onSelect} className="axi-row"
      style={{
        display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
        border: 'none', borderLeft: `3px solid ${selected ? NAVY : 'transparent'}`,
        background: selected ? '#eef4f8' : undefined,
        padding: '10px 12px', borderBottom: `1px solid ${LINE}`, position: 'relative',
      }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
        <span style={{ color: kind === 'strength' ? '#2AA147' : NAVY, fontWeight: 800, fontSize: 13, flexShrink: 0 }}>
          {kind === 'strength' ? '✓' : index + 1}
        </span>
        <span style={{ color: mv ? MUTE : INK, fontWeight: 600, fontSize: 14, lineHeight: 1.3, paddingRight: 14 }}>
          {itemLabel(item)}
        </span>
        <span className="axi-chev" style={{ position: 'absolute', right: 10, top: 11, color: selected ? NAVY : '#b6c2cc', fontSize: 15, fontWeight: 700 }}>›</span>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 3, paddingLeft: 21 }}>
        {isMain && <span style={{ color: '#c23b34', fontWeight: 700, fontSize: 10.5, letterSpacing: .3 }}>MAIN DRAG ·</span>}
        <span style={{ color: MUTE, fontSize: 12 }}>{locLabel(item)}</span>
        {mv && <span style={{ color: MUTE, fontSize: 11, fontStyle: 'italic' }}>· not written up</span>}
      </div>
    </button>
  );
}

// Composer read — the 5-part explanation, in fixed order, with source units behind an expander. Replaces
// the concise narration for patterns that composed + passed the gate/validator (accepted). ~100-180 words.
const COMPOSED_ORDER = ['pattern', 'content_anchor', 'mechanism', 'counterweight_or_limit', 'writer_implication'];
function ComposedRead({ composed }) {
  const [open, setOpen] = useState(false);
  const c = composed?.parsed?.components || {};
  const parts = COMPOSED_ORDER.map(k => c[k]).filter(x => x && x.text);
  if (!parts.length) return null;
  const refs = [...new Set(parts.flatMap(p => p.source_refs || []))];
  return (
    <div style={{ marginTop: 10 }}>
      {parts.map((p, i) => (
        <p key={i} style={{ margin: i ? '.5rem 0 0' : 0, color: INK, fontSize: 14.5, lineHeight: 1.6 }}>{p.text}</p>
      ))}
      {refs.length > 0 && (
        <>
          <button type="button" onClick={() => setOpen(o => !o)}
            style={{ all: 'unset', cursor: 'pointer', marginTop: 8, color: NAVY, fontSize: 12.5, fontWeight: 600 }}>
            {open ? '▾ source units' : `▸ source units (${refs.length})`}
          </button>
          {open && <div style={{ marginTop: 6, color: MUTE, fontSize: 12.5, lineHeight: 1.5 }}>{refs.map(r => refLabel(r)).join(' · ')}</div>}
        </>
      )}
    </div>
  );
}

// ---- RIGHT: the expanded diagnosis --------------------------------------------------------------
function Detail({ item, kind, onAsk, composed }) {
  if (!item) return null;
  const mv = item.narration_status === 'machine_view';
  const rep = item.representative_example || {};
  const con = item.contrast_example || {};
  const questions = item.questions_for_ai || [];
  const challenge = questions.find(q => (q.mode || q.slot_key) === 'challenge');
  const others = questions.filter(q => q !== challenge);

  return (
    <div style={{ padding: '16px 18px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
        <div style={{ color: INK, fontWeight: 700, fontSize: 17, lineHeight: 1.25 }}>
          {itemLabel(item)}
        </div>
        <div style={{ color: NAVY, fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>{locLabel(item)}</div>
      </div>

      {mv ? (
        <div style={{ marginTop: 12, color: MUTE, fontSize: 14, lineHeight: 1.6, fontStyle: 'italic' }}>
          A recurring pattern shows here across {scopeLabel(item)}, but it hasn’t been written up yet —
          its span is lit on the timeline above.
        </div>
      ) : (
        <>
          {/* composer read replaces the concise narration when this pattern composed + was accepted */}
          {composed
            ? <ComposedRead composed={composed} />
            : (item.plain_pattern && (
              <p style={{ marginTop: 10, color: INK, fontSize: 14.5, lineHeight: 1.6 }}>{item.plain_pattern}</p>
            ))}

          {/* THE SCENE — the moment of theater */}
          {(rep.quote || rep.example_read) && (
            <div style={{ marginTop: 14, borderLeft: `3px solid ${NAVY}`, paddingLeft: 12 }}>
              <div style={{ color: NAVY, fontWeight: 700, fontSize: 12.5, marginBottom: 4 }}>{rep.label || refLabel(rep.unit_ref)}</div>
              {rep.quote && (
                <div style={{ color: INK, fontSize: 14.5, lineHeight: 1.5, fontStyle: 'italic' }}>“{rep.quote}”</div>
              )}
              {rep.example_read && (
                <div style={{ color: MUTE, fontSize: 13, lineHeight: 1.5, marginTop: 5 }}>{rep.example_read}</div>
              )}
            </div>
          )}

          {/* (Removed) the "where it lands" / "where it slips" counter-example: a positive counter-example
              nested inside a PROBLEM (styled like the problem's own example) read as another problem
              instance and confused readers. A problem now shows just the pattern, its example, and the fix;
              "where it already works" belongs with the strengths, not inside a problem. */}

          {/* ACTION */}
          {item.action?.action && (
            <div style={{ marginTop: 14 }}>
              <div style={{ color: NAVY, fontWeight: 700, fontSize: 12.5, marginBottom: 3 }}>
                {kind === 'strength' ? 'CARRY IT FORWARD' : 'TRY THIS'}
              </div>
              <div style={{ color: INK, fontSize: 14, lineHeight: 1.55 }}>{item.action.action}</div>
              {item.action.test && <div style={{ color: MUTE, fontSize: 13, lineHeight: 1.5, marginTop: 4 }}>Check: {item.action.test}</div>}
            </div>
          )}
        </>
      )}

      {/* Challenge = the alternate read, elevated. A QUESTION, never an asserted second diagnosis. */}
      {challenge && (
        <button type="button" onClick={() => onAsk && onAsk(challenge, item)}
          style={{ display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
            marginTop: 16, background: '#fbf7ee', border: '1px solid #e6d9bd', borderRadius: 10, padding: '11px 13px' }}>
          <div style={{ color: '#9a6700', fontWeight: 700, fontSize: 11.5, letterSpacing: .3, marginBottom: 4 }}>ANOTHER WAY TO READ IT</div>
          <div style={{ color: INK, fontSize: 14, lineHeight: 1.5 }}>{challenge.question}</div>
          <div style={{ color: NAVY, fontSize: 12.5, fontWeight: 600, marginTop: 6 }}>ask →</div>
        </button>
      )}

      {/* other questions → chat handoff chips */}
      {others.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
          {others.map(q => (
            <button key={q.question_id || q.question} type="button" onClick={() => onAsk && onAsk(q, item)}
              style={{ cursor: 'pointer', background: '#fff', border: `1px solid ${NAVY}`, color: NAVY,
                borderRadius: 999, padding: '6px 12px', fontSize: 12.5, fontWeight: 600, lineHeight: 1.3, textAlign: 'left' }}>
              {q.question}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---- the panel ----------------------------------------------------------------------------------
export default function AxisInspector({ axisLabel, inspector, explanation = null, onShowOnTimeline, onAsk, preselectFirst = true }) {
  const merged = inspector?.merged || {};
  // composer explanations for this axis, keyed by pattern_id, ACCEPTED only (gate + validator passed).
  const acceptedComposed = useMemo(() => {
    const m = {};
    (explanation?.explanations || []).forEach(e => { if (e && e.accepted && e.pattern_id) m[e.pattern_id] = e; });
    return m;
  }, [explanation]);
  const status = inspector?.status || merged.generation?.status;
  const problems = merged.problem_patterns || [];
  const strengths = merged.strength_patterns || [];
  const items = useMemo(() => [
    ...problems.map(p => ({ p, kind: 'issue' })),
    ...strengths.map(p => ({ p, kind: 'strength' })),
  ], [problems, strengths]);

  const [sel, setSel] = useState(0);
  useEffect(() => { setSel(0); }, [axisLabel, inspector]);
  // selecting an item both opens its detail (tree) AND lights its footprint on the timeline (forest link)
  const selectItem = (i, p) => { setSel(i); if (onShowOnTimeline) onShowOnTimeline(patternRefs(p), p.candidate_id, itemLabel(p)); };

  // preselect the first issue's timeline highlight on open (gentle — tied to the highlighted card)
  useEffect(() => {
    if (preselectFirst && items[0] && onShowOnTimeline) onShowOnTimeline(patternRefs(items[0].p), items[0].p.candidate_id, itemLabel(items[0].p));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [axisLabel]);

  const qr = merged.quick_read || {};
  const owed = qr.owed_status;
  const notOwed = owed && owed !== 'genuinely_owed' && owed !== 'owed' && owed !== 'strongly_owed';
  const verdict = merged.overall_read?.paragraphs?.[0]?.text || qr.hover_summary || '';
  const cov = merged.pattern_coverage || {};

  // ---- non-ready states ----
  if (!inspector) {
    return <Shell axisLabel={axisLabel} band={qr.band} score={qr.score}>
      <Empty text={`No ${axisLabel} analysis yet. The timeline above still shows where the axis reads weak.`} /></Shell>;
  }
  if (status === 'generating' || status === 'skeleton') {
    return <Shell axisLabel={axisLabel} band={qr.band} score={qr.score}>
      <Empty text={status === 'generating' ? `Analyzing ${axisLabel}…` : `${axisLabel} isn’t written up yet — the map above shows the shape.`} /></Shell>;
  }
  if (notOwed) {
    return <Shell axisLabel={axisLabel} band={qr.band} score={qr.score} verdict={verdict || `This script doesn’t lean on ${axisLabel} — nothing to fix here.`}>
      <div style={{ padding: '18px', color: MUTE, fontSize: 14, lineHeight: 1.6 }}>
        {axisLabel} isn’t strongly owed by this script, so there’s no problem to chase.
        {strengths[0] && <> One stretch even holds it: <b style={{ color: INK }}>{strengths[0].title}</b>.</>}
      </div></Shell>;
  }

  const nIssue = problems.length, nProt = strengths.length;
  const selItem = items[sel]?.p;
  const selKind = items[sel]?.kind;

  return (
    <Shell axisLabel={axisLabel} band={qr.band} score={qr.score}
      verdict={verdict}
      counts={`${nIssue} issue${nIssue === 1 ? '' : 's'}${nProt ? ` · ${nProt} protected` : ''}`}
      coverage={cov.headline_coverage}
      stale={status === 'stale'}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'stretch' }}>
        {/* LEFT — the forest */}
        <div style={{ flex: '1 1 260px', minWidth: 240, maxWidth: 360, borderRight: `1px solid ${LINE}` }}>
          {problems.length > 0 && <SectionLabel>Issues</SectionLabel>}
          {problems.map((p, i) => (
            <ListRow key={p.candidate_id || i} item={p} index={i} kind="issue"
              selected={sel === i} onSelect={() => selectItem(i, p)} />
          ))}
          {strengths.length > 0 && <SectionLabel>Protect</SectionLabel>}
          {strengths.map((p, i) => (
            <ListRow key={p.candidate_id || `s${i}`} item={p} index={i} kind="strength"
              selected={sel === problems.length + i} onSelect={() => selectItem(problems.length + i, p)} />
          ))}
        </div>
        {/* RIGHT — the tree */}
        <div style={{ flex: '3 1 380px', minWidth: 300 }}>
          <Detail item={selItem} kind={selKind} onAsk={onAsk}
            composed={selItem ? acceptedComposed[selItem.candidate_id] : null} />
        </div>
      </div>
      {cov.headline_coverage != null && problems.length > 0 && (
        <div style={{ padding: '10px 16px', borderTop: `1px solid ${LINE}`, color: MUTE, fontSize: 12.5 }}>
          {nIssue === 1 ? 'This issue explains' : 'These issues explain'} {coverageWord(cov.headline_coverage)} of the {axisLabel} drag.
        </div>
      )}
    </Shell>
  );
}

function coverageWord(c) {
  if (c >= 0.85) return 'nearly all';
  if (c >= 0.6) return 'most';
  if (c >= 0.35) return 'much';
  return 'part';
}

function SectionLabel({ children }) {
  return <div style={{ color: NAVY, fontWeight: 700, fontSize: 12.5, letterSpacing: .3, padding: '10px 12px 6px' }}>{children}</div>;
}
function Empty({ text }) {
  return <div style={{ padding: '28px 18px', color: MUTE, fontSize: 14.5, lineHeight: 1.6 }}>{text}</div>;
}

function Shell({ axisLabel, band, score, verdict, counts, coverage, stale, children }) {
  const b = BAND[band] || BAND.not_owed;
  return (
    <div style={{ fontFamily: 'system-ui, sans-serif' }}>
      <style>{`.axi-row:hover{background:#f1f6fa;} .axi-row:hover .axi-chev{color:${NAVY};}`}</style>
      {stale && (
        <div style={{ background: '#fbf3e6', color: '#9a6700', fontSize: 12.5, fontWeight: 600, padding: '6px 16px', borderBottom: `1px solid #e6d9bd` }}>
          This explanation is out of date — regenerate to refresh it.
        </div>
      )}
      {/* header — verdict kept to a readable measure on the left; a glance block uses the right so the
          width is balanced instead of empty. */}
      <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', padding: '16px 18px 14px', borderBottom: `1px solid ${LINE}` }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: NAVY, fontWeight: 800, fontSize: 20, letterSpacing: .2 }}>{axisLabel}</div>
          {verdict && <div style={{ color: INK, fontSize: 15, lineHeight: 1.5, marginTop: 8, maxWidth: 640 }}>{verdict}</div>}
          {counts && <div style={{ color: MUTE, fontSize: 12.5, fontWeight: 600, marginTop: 8, textTransform: 'uppercase', letterSpacing: .4 }}>{counts}</div>}
        </div>
        {score != null && (
          <div style={{ flexShrink: 0, textAlign: 'right', minWidth: 96, borderLeft: `1px solid ${LINE}`, paddingLeft: 16 }}>
            <div style={{ color: b.c, fontWeight: 800, fontSize: 30, lineHeight: 1 }}>{fmtScore(score)}</div>
            <div style={{ color: b.c, fontWeight: 700, fontSize: 13, marginTop: 2 }}>{b.w}</div>
            <div style={{ color: MUTE, fontSize: 11, marginTop: 1 }}>/ 10</div>
            {coverage != null && (
              <div style={{ marginTop: 10, color: MUTE, fontSize: 11.5, lineHeight: 1.35 }}>
                <b style={{ color: INK, fontWeight: 700 }}>{Math.round(coverage * 100)}%</b><br />of the drag<br />explained
              </div>
            )}
          </div>
        )}
      </div>
      {children}
    </div>
  );
}

AxisInspector.propTypes = {
  axisLabel: PropTypes.string.isRequired,
  inspector: PropTypes.object,        // { status, merged }
  onShowOnTimeline: PropTypes.func,   // (unitRefs[], candidateId) => void
  onAsk: PropTypes.func,              // (questionObj, patternObj) => void
  preselectFirst: PropTypes.bool,
};
